import {gql} from '@apollo/client'
import React, {useState} from 'react'
import {Helmet} from 'react-helmet-async'
import styled from 'styled-components'
import {ListLayout} from '..'
import {Avatar} from '../../../../components/library/avatar/avatar'
import {Button} from '../../../../components/library/buttons'
import {List, ListColumns} from '../../../../components/library/list'
import {Tag} from '../../../../components/library/tag/tag'
import {
  BusinessUserStatus,
  PermissionAction,
  PermissionResource
} from '../../../../data/workbench-api/generated/types'
import {toTitleCase} from '../../../../lib/helpers'
import {useToast} from '../../../../lib/toast'
import {OrganisationNode} from '../../../../organisation'
import {
  useWorkbenchMembersQuery,
  WorkbenchMembersFragment
} from './generated/index'
import {InviteLinkModalMemberFragment} from './generated/invite-link-modal'
import {MemberModalFragment} from './generated/member-modal'
import {InviteLinkModal} from './invite-link-modal'
import {MemberModal} from './member-modal'
import {RowOptions} from './row-options'

const Primary = styled.div`
  color: ${p => p.theme.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -5px;
  cursor: pointer;
`

const Text = styled.div`
  display: flex;
  margin-left: 10px;
`

// temporary, will be removed once we can retrieve an actual id for the member
type WorkbenchMembersFragmentWithId = WorkbenchMembersFragment & {id: string}

export const WorkbenchMembers = ({
  organisation
}: {
  organisation: OrganisationNode
}) => {
  const {data, loading, refetch} = useWorkbenchMembersQuery({
    variables: {organisationId: organisation.id}
  })
  const [editingMember, setEditingMember] = useState<
    MemberModalFragment | 'new'
  >()
  const [inviteLink, setInviteLink] = useState<InviteLinkModalMemberFragment>()
  const toast = useToast()
  const canEdit = organisation.hasPermission(
    PermissionResource.BusinessUser,
    PermissionAction.Edit
  )

  const columns: ListColumns<WorkbenchMembersFragmentWithId> = [
    {
      id: 'member',
      header: 'Workbench member',
      accessor: r => {
        const name = r.firstName
          ? `${r.firstName} ${r.lastName || ''}`
          : r.email
        return (
          <Primary onClick={() => setEditingMember(r)}>
            <Avatar name={r.email} />
            <Text>
              <a>{name}</a>
            </Text>
          </Primary>
        )
      }
    },
    {
      id: 'email',
      header: 'Email',
      accessor: r => r.email
    },
    {
      id: 'user-groups',
      header: 'Group/s assigned to',
      accessor: r =>
        r.groups.nodes.map(n => n.organisationGroup?.name).join(', ')
    },
    {
      id: 'status',
      header: 'Status',
      accessor: r => {
        switch (r.status) {
          case BusinessUserStatus.Active:
            return <Tag appearance="green">{toTitleCase(r.status)}</Tag>
          case BusinessUserStatus.Inactive:
          case BusinessUserStatus.InviteCancelled:
            return (
              <Tag appearance="light">
                {toTitleCase(r.status.replace('_', ' '))}
              </Tag>
            )
          case BusinessUserStatus.Invited:
            return r.inviteExpired ? (
              <Tag appearance="red">Invite Expired</Tag>
            ) : (
              <Tag appearance="blue">Invited</Tag>
            )
        }
      }
    }
  ]

  return (
    <>
      <Helmet>
        <title>Workbench members</title>
      </Helmet>
      <ListLayout
        header={
          <>
            <p>
              Workbench members can be given membership to one or more groups.
              Based on those groups they'll have access to specific features,
              actions and environments.
            </p>
            <Button disabled={!canEdit} onClick={() => setEditingMember('new')}>
              Invite new member
            </Button>
          </>
        }
      >
        <List
          bleed={'#f2f2f2'}
          loading={loading && !data}
          columns={columns}
          rows={data?.businessUsers?.nodes ?? []}
          renderOverflow={(member, closeDropdown) => (
            <RowOptions
              organisation={organisation}
              member={member}
              closeDropdown={closeDropdown}
              refetch={refetch}
              editMember={() => setEditingMember(member)}
              openInviteLinkModal={() => setInviteLink(member)}
            />
          )}
        />
      </ListLayout>
      {editingMember && (
        <MemberModal
          organisation={organisation}
          groups={data?.groups?.nodes ?? []}
          member={editingMember === 'new' ? undefined : editingMember}
          handleClose={() => setEditingMember(undefined)}
          onSaved={async () => {
            await refetch()
            toast(editingMember === 'new' ? 'Member invited' : 'Member updated')
            setEditingMember(undefined)
          }}
          existingEmails={
            (data?.businessUsers?.nodes.map(u => u.email).filter(Boolean) as
              | string[]
              | undefined) ?? []
          }
        />
      )}
      {inviteLink && (
        <InviteLinkModal
          member={inviteLink}
          handleClose={() => setInviteLink(undefined)}
        />
      )}
    </>
  )
}

WorkbenchMembers.fragments = {
  row: gql`
    fragment WorkbenchMembers on BusinessUser {
      id
      accountId
      status
      firstName
      lastName
      email
      inviteExpired
      groups: businessUserGroupsByOrganisationIdAndBusinessUserId {
        nodes {
          organisationGroup {
            name
          }
        }
      }
      ...MemberRowOptions
      ...MemberModal
      ...InviteLinkModalMember
    }
    ${RowOptions.fragments.member}
    ${MemberModal.fragments.member}
    ${InviteLinkModal.fragments.member}
  `
}

gql`
  query WorkbenchMembers(
    $organisationId: String!
    $orderBy: [BusinessUsersOrderBy!]! = [FIRST_NAME_ASC]
  ) {
    businessUsers(
      condition: {organisationId: $organisationId}
      orderBy: $orderBy
    ) {
      nodes {
        accountId
        ...WorkbenchMembers
      }
    }
    groups(condition: {organisationId: $organisationId}) {
      nodes {
        ...Groups
      }
    }
  }
  ${WorkbenchMembers.fragments.row}
  ${MemberModal.fragments.groups}
`
